import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Video, Loader2, Trash2, Target, Lock, Film, CheckCircle2, Clock,
  Plus, Download, Link2, Send, Clapperboard, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { VideoStudioModal } from '../components/VideoStudioModal';
import { PostVideoDialog } from '../components/PostVideoDialog';
import { VideoTimelineModal } from '../components/VideoTimelineModal';

const BACKEND_URL = (() => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  return (!raw || raw === 'undefined') ? '' : raw;
})();

const STATUS_STYLES = {
  draft: { label: 'Draft', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock },
  ready: { label: 'Ready', cls: 'bg-sky-50 text-sky-700 border-sky-200', icon: CheckCircle2 },
  attached: { label: 'Attached', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const mediaUrl = (m) => {
  if (!m?.url) return null;
  return m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`;
};

// Force a real file download with a proper .mp4 name so the clip saves correctly
// on mobile and uploads to TikTok (an <a download> to a video URL often plays
// inline / saves with the wrong extension instead of downloading).
const downloadFinal = async (proj) => {
  const m = proj.final_media;
  const url = mediaUrl(m);
  if (!url) return;
  const isWebm = (m.filename || m.url || '').toLowerCase().includes('.webm');
  const ext = isWebm ? 'webm' : 'mp4';
  const name = `${(proj.name || 'video').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'video'}.${ext}`;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 4000);
  } catch (_) {
    window.open(url, '_blank', 'noopener');
  }
};

export const VideoLibraryPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [studioOpen, setStudioOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);    // project id being edited
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [attachTarget, setAttachTarget] = useState(null); // project being attached
  const [attachPostId, setAttachPostId] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [postTarget, setPostTarget] = useState(null);     // project being posted directly

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/video/library');
      setProjects(res.data.projects || []);
      setLocked(false);
    } catch (e) {
      if (e.response?.status === 403) {
        setLocked(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load video library');
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => {
    const onComplete = () => fetchProjects();
    window.addEventListener('video-render-complete', onComplete);
    return () => window.removeEventListener('video-render-complete', onComplete);
  }, [fetchProjects]);
  useEffect(() => {
    api.get('/posts?limit=200').then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, [api]);

  const remove = async (project) => {
    if (!window.confirm('Delete this video project? Source clips will be removed. This cannot be undone.')) return;
    setBusyId(project.id);
    try {
      await api.delete(`/video/projects/${project.id}`);
      toast.success('Video project deleted');
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const confirmAttach = async () => {
    if (!attachTarget || !attachPostId) return;
    setAttaching(true);
    try {
      await api.post(`/video/projects/${attachTarget.id}/attach`, { campaign_post_id: attachPostId });
      toast.success('Video attached to the campaign post');
      setAttachTarget(null);
      setAttachPostId('');
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not attach video');
    } finally {
      setAttaching(false);
    }
  };

  if (locked) {
    return (
      <div className="space-y-6" data-testid="video-library-page">
        <Header onNew={() => {}} hideNew />
        <Card className="border-dashed border-amber-200 bg-amber-50/40" data-testid="video-library-locked">
          <CardContent className="p-12 text-center">
            <Lock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-zinc-900">Video Studio is a Growth & Pro feature</p>
            <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
              Record, edit, caption, and attach social videos. Upgrade your plan to unlock the Video Studio.
            </p>
            <Button className="mt-5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate('/subscription')} data-testid="video-library-upgrade-btn">
              Upgrade plan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="video-library-page">
      <Header onNew={() => setStudioOpen(true)} onTimeline={() => setTimelineOpen(true)} />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : projects.length === 0 ? (
        <Card className="border-dashed border-zinc-200">
          <CardContent className="p-12 text-center">
            <Film className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-700">No videos yet</p>
            <p className="text-xs text-zinc-500 mt-1 max-w-sm mx-auto">
              Record, upload, or capture from your phone — captions and all. You can attach the result to a campaign now or later.
            </p>
            <Button className="mt-5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setStudioOpen(true)} data-testid="video-library-new-empty-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New video
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="video-library-grid">
          {projects.map((proj) => {
            const status = STATUS_STYLES[proj.status] || STATUS_STYLES.draft;
            const StatusIcon = status.icon;
            const final = proj.final_media;
            const previewMedia = final || (proj.source_media || []).slice(-1)[0] || null;
            const preview = mediaUrl(previewMedia);
            const clipCount = (proj.source_media || []).length;
            const isAttached = proj.status === 'attached' && proj.post_exists;
            return (
              <Card key={proj.id} className="border-zinc-200 hover:shadow-md transition-shadow overflow-hidden" data-testid={`video-card-${proj.id}`}>
                <div className="aspect-video bg-black flex items-center justify-center">
                  {preview ? (
                    <video src={preview} controls preload="metadata" className="w-full h-full object-contain" data-testid={`video-preview-${proj.id}`} />
                  ) : (
                    <Film className="w-10 h-10 text-zinc-600" />
                  )}
                </div>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className={status.cls}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {status.label}
                    </Badge>
                    {final?.format && <span className="text-[10px] text-zinc-400 font-medium">{final.format}{final.captioned ? ' · CC' : ''}</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-zinc-900 leading-tight line-clamp-2 mt-2">{proj.name || 'Untitled video'}</h3>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="text-[11px] text-zinc-500 space-y-0.5">
                    {proj.post_title ? (
                      <div className="line-clamp-1">Post: <span className="text-zinc-700">{proj.post_title}</span></div>
                    ) : (
                      <div className="text-zinc-400">Standalone video</div>
                    )}
                    <div>{clipCount} source clip{clipCount === 1 ? '' : 's'}</div>
                    <div>Updated: {formatDate(proj.updated_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {final ? (
                      <>
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setPostTarget(proj)} data-testid={`video-post-${proj.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Post directly
                        </Button>
                        {isAttached ? (
                          <Button size="sm" variant="outline" onClick={() => navigate('/campaigns')} data-testid={`video-go-post-${proj.id}`}>
                            <Target className="w-3.5 h-3.5 mr-1" /> Go to campaign
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setAttachTarget(proj); setAttachPostId(''); }} data-testid={`video-attach-${proj.id}`}>
                            <Link2 className="w-3.5 h-3.5 mr-1" /> Attach to post
                          </Button>
                        )}
                        <button type="button" onClick={() => downloadFinal(proj)} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline px-2 py-1" data-testid={`video-download-${proj.id}`}>
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                      </>
                    ) : (
                      <span className="text-[11px] text-zinc-400">No rendered clip yet</span>
                    )}
                    {proj.status !== 'attached' && (
                      <Button size="sm" variant="outline" onClick={() => setEditTarget(proj.id)} data-testid={`video-edit-${proj.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 ml-auto"
                      onClick={() => remove(proj)}
                      disabled={busyId === proj.id}
                      data-testid={`video-delete-${proj.id}`}
                    >
                      {busyId === proj.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Standalone studio */}
      <VideoStudioModal
        open={studioOpen}
        onClose={() => setStudioOpen(false)}
        onSaved={fetchProjects}
      />

      {/* Edit an existing draft/project */}
      <VideoStudioModal
        open={!!editTarget}
        projectId={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={fetchProjects}
      />

      {/* A-roll timeline editor */}
      <VideoTimelineModal
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        onSaved={fetchProjects}
      />

      {/* Post directly as its own social post */}
      <PostVideoDialog
        project={postTarget}
        open={!!postTarget}
        onClose={() => setPostTarget(null)}
        onPosted={fetchProjects}
      />

      {/* Attach-to-post dialog (for library cards) */}
      <Dialog open={!!attachTarget} onOpenChange={(o) => { if (!o) { setAttachTarget(null); setAttachPostId(''); } }}>
        <DialogContent data-testid="library-attach-dialog">
          <DialogHeader>
            <DialogTitle>Attach video to a campaign post</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Select value={attachPostId} onValueChange={setAttachPostId}>
              <SelectTrigger data-testid="library-attach-post-select"><SelectValue placeholder="Choose a campaign post…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {posts.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {(p.post_title || p.topic || 'Untitled').slice(0, 44)} · {p.platform}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAttachTarget(null); setAttachPostId(''); }}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!attachPostId || attaching} onClick={confirmAttach} data-testid="library-attach-confirm-btn">
              {attaching ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Link2 className="w-4 h-4 mr-1.5" />} Attach
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Header = ({ onNew, onTimeline, hideNew }) => (
  <div className="flex items-start justify-between gap-3">
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
        <Video className="w-6 h-6 text-indigo-600" />
        Video Studio
      </h1>
      <p className="text-zinc-500 text-sm mt-0.5">
        Record, caption, and manage your videos — attach to a campaign now or later.
      </p>
    </div>
    {!hideNew && (
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" onClick={onTimeline} data-testid="video-library-timeline-btn">
          <Clapperboard className="w-4 h-4 mr-1.5" /> Timeline
        </Button>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onNew} data-testid="video-library-new-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New video
        </Button>
      </div>
    )}
  </div>
);

export default VideoLibraryPage;
